// frontend/src/pages/FuneralGuide.tsx
// The Funeral Guide — paid add-on ($99.95 per license / per person honoured).
//
// Routes:
//   /funeral-guide              → license picker (teaser CTA lands here)
//   /funeral-guide/:licenseId   → the guide itself
//
// The paid content (guide sections, service template, music library) is NEVER
// bundled into this file — it is fetched from the authenticated, purchase-gated
// POST /api/funeral-guide/content endpoint. Before purchase, that endpoint
// returns 402 and this page renders the purchase card; the buy button starts a
// Stripe Checkout via POST /api/stripe/create-funeral-guide-checkout.
//
// Deliberately a React page (not static HTML) so Clerk auth is native — the
// static-page Clerk-key bug class can't reach it.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NAVY = '#19285c';
const GREY = '#8c83b4';
const PINK = '#d4adbb';
const WARM = '#eae8e4';
const BODY = '#2a2a2a';

const ICON = (name: string) => `/Assets/200-handdrawn-icons-by-heloholo/png/48/${name}48.png`;
const NAVY_FILTER = 'brightness(0) saturate(100%) invert(15%) sepia(30%) saturate(2000%) hue-rotate(200deg) brightness(90%) contrast(95%)';

interface GuideSection {
  id: string; number: string; title: string; paragraphs: string[];
  callout?: { title: string; text: string };
  tips?: { title: string; text: string }[];
}
interface TemplateElement {
  id: string; title: string; guidance: string; script?: string; musicCue?: string; optional?: boolean;
}
interface SongRow { title: string; artist: string; amazonUrl: string }
interface MusicCategory { key: string; label: string; songs: SongRow[] }
interface ContentResponse {
  success: boolean;
  error?: string;
  deceasedName?: string | null;
  isOwner?: boolean;
  priceAud?: string;
  guide?: { sections: GuideSection[]; template: TemplateElement[] };
  music?: { intro: string[]; categories: MusicCategory[] };
}
interface LicenseSummary {
  id: string;
  deceased_person_name: string | null;
  funeral_guide_purchased_at: string | null;
  archived_at: string | null;
}

// ---- Service Builder (Slice 2) ----
// One plan per license, stored as sections JSONB via /api/service-plan/get + save.
interface ReadingRow {
  title: string;
  author: string;
  readBy: string;
  // Full text is printed in the booklet ONLY when supplied — for in-copyright
  // poems the family leaves this empty and the booklet lists title + author.
  text: string;
}
interface MomentRow { title: string; detail: string; music: string }
interface PlanElementState {
  included: boolean;
  script?: string;
  music?: string;
  reader?: string;
  speakers?: { name: string; relationship: string }[];
  notes?: string;
  livestream?: boolean;
  printInBooklet?: boolean;       // eulogy: print the full text in the booklet
  readings?: ReadingRow[];        // readings & poems
  moments?: MomentRow[];          // the family's own moments
}
interface BookletPhoto { id: string; caption: string }
interface PlanDetails { serviceLine: string; venue: string; celebrant: string }
interface PlanSections {
  version: 1;
  elements: Record<string, PlanElementState>;
  // Booklet photo selection — adapts to however many the family chooses:
  // none selected → the booklet simply has no photo pages.
  photos?: { coverPhotoId: string | null; items: BookletPhoto[] };
  details?: PlanDetails;
}
interface LibraryPhoto {
  id: string;
  public_url: string;
  thumbnail_url?: string | null;
  restored_url?: string | null;
  caption?: string | null;
}

// Build the default plan from the template, with the person's first name woven
// into the pre-filled scripts. Saved state merges OVER these defaults.
function defaultPlan(template: TemplateElement[], personName: string): PlanSections {
  const first = (personName || '').trim().split(/\s+/)[0] || 'your loved one';
  const elements: Record<string, PlanElementState> = {};
  for (const el of template) {
    elements[el.id] = {
      included: true,
      ...(el.script ? { script: el.script.split('[Name]').join(first) } : {}),
      ...(el.musicCue ? { music: '' } : {}),
      ...(el.id === 'eulogy' ? { reader: '', printInBooklet: true } : {}),
      ...(el.id === 'tributes' ? { speakers: [] } : {}),
      ...(el.id === 'readings' ? { readings: [] } : {}),
      ...(el.id === 'custom-moments' ? { moments: [] } : {}),
      ...(el.id === 'welcome' || el.id === 'farewell' ? { notes: '' } : {}),
      ...(el.id === 'housekeeping' ? { livestream: false } : {}),
    };
  }
  return { version: 1, elements, photos: { coverPhotoId: null, items: [] }, details: { serviceLine: '', venue: '', celebrant: '' } };
}

const serif = "'Playfair Display', Georgia, serif";
const sans = "'Inter', system-ui, sans-serif";

export default function FuneralGuide() {
  const { licenseId } = useParams<{ licenseId?: string }>();
  const { isLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();

  const [phase, setPhase] = useState<'loading' | 'pick' | 'locked' | 'unlocked' | 'error'>('loading');
  const [content, setContent] = useState<ContentResponse | null>(null);
  const [licenses, setLicenses] = useState<LicenseSummary[]>([]);
  const [personName, setPersonName] = useState<string>('');
  const [tab, setTab] = useState<'guide' | 'template' | 'music' | 'build'>('guide');
  const [musicFilter, setMusicFilter] = useState<string>('all');
  const [buying, setBuying] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Service Builder state
  const [plan, setPlan] = useState<PlanSections | null>(null);
  const [canWrite, setCanWrite] = useState(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [pickerFor, setPickerFor] = useState<string | null>(null); // element id with the song picker open
  const [pickerFilter, setPickerFilter] = useState('all');
  const [libraryPhotos, setLibraryPhotos] = useState<LibraryPhoto[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const planRef = useRef<PlanSections | null>(null);
  planRef.current = plan;

  // No licenseId → show the picker (fed by /api/subscription/status).
  const loadPicker = useCallback(async () => {
    try {
      const token = await getToken();
      const resp = await fetch('/api/subscription/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      const ls: LicenseSummary[] = (data.licenses || []).map((l: any) => ({
        id: l.id,
        deceased_person_name: l.deceased_person_name || null,
        funeral_guide_purchased_at: l.funeral_guide_purchased_at || null,
        archived_at: l.archived_at || null,
      }));
      setLicenses(ls);
      setPhase('pick');
    } catch {
      setErrMsg('We couldn’t load your pieces just now. Please try again.');
      setPhase('error');
    }
  }, [getToken]);

  const loadContent = useCallback(async (id: string) => {
    try {
      const token = await getToken();
      const resp = await fetch('/api/funeral-guide/content', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId: id }),
      });
      const data: ContentResponse = await resp.json();
      if (resp.status === 402) {
        setContent(data);
        setPersonName(data.deceasedName || '');
        setPhase('locked');
        return;
      }
      if (!resp.ok || !data.success) {
        setErrMsg(resp.status === 404 ? 'We couldn’t find that piece.' : (data.error || 'Something went wrong.'));
        setPhase('error');
        return;
      }
      setContent(data);
      setPersonName(data.deceasedName || '');
      setPhase('unlocked');
    } catch {
      setErrMsg('We couldn’t load the guide just now. Please check your connection and try again.');
      setPhase('error');
    }
  }, [getToken]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      // Carry the intent through sign-in so the visitor comes back HERE
      // afterwards, not to the dashboard (real-user report, 2026-07-17).
      window.location.href = '/sign-in?redirect_url=' + encodeURIComponent(window.location.pathname);
      return;
    }
    if (licenseId) loadContent(licenseId);
    else loadPicker();
  }, [isLoaded, isSignedIn, licenseId, loadContent, loadPicker]);

  // Load (or lazily create) the service plan once the guide is unlocked.
  // Saved state merges OVER the template defaults so new template elements
  // added later still appear for existing plans.
  const loadPlan = useCallback(async (id: string, template: TemplateElement[], name: string) => {
    try {
      const token = await getToken();
      const resp = await fetch('/api/service-plan/get', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId: id }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) return; // builder tab shows a retry note
      const defaults = defaultPlan(template, name);
      const saved: PlanSections | null = data.plan?.sections || null;
      if (saved?.elements) {
        for (const [key, val] of Object.entries(saved.elements)) {
          defaults.elements[key] = { ...defaults.elements[key], ...(val as PlanElementState) };
        }
      }
      if (saved?.photos) defaults.photos = saved.photos;
      if (saved?.details) defaults.details = { ...defaults.details!, ...saved.details };
      setCanWrite(data.canWrite !== false);
      setPlan(defaults);
      // Load the family's uploaded photos for the booklet chooser (best-effort).
      try {
        const token2 = await getToken();
        const presp = await fetch(`/api/photos/list?testamentLicenseId=${encodeURIComponent(id)}`, {
          headers: { Authorization: `Bearer ${token2}` },
        });
        if (presp.ok) {
          const pdata = await presp.json();
          setLibraryPhotos(pdata.photos || []);
        }
      } catch { /* photo panel shows the upload pointer instead */ }
    } catch {
      /* builder tab shows a retry note when plan is null */
    }
  }, [getToken]);

  useEffect(() => {
    if (phase === 'unlocked' && licenseId && content?.guide?.template && !plan) {
      loadPlan(licenseId, content.guide.template, content.deceasedName || '');
    }
  }, [phase, licenseId, content, plan, loadPlan]);

  // Debounced autosave (CyranoEditor pattern): any change schedules a save
  // 1.5s out; the latest state wins.
  const scheduleSave = useCallback(() => {
    if (!licenseId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const token = await getToken();
        const resp = await fetch('/api/service-plan/save', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ licenseId, sections: planRef.current }),
        });
        setSaveState(resp.ok ? 'saved' : 'error');
      } catch {
        setSaveState('error');
      }
    }, 1500);
  }, [licenseId, getToken]);

  const updateElement = useCallback((elId: string, patch: Partial<PlanElementState>) => {
    setPlan((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        elements: { ...prev.elements, [elId]: { ...prev.elements[elId], ...patch } },
      };
    });
    scheduleSave();
  }, [scheduleSave]);

  const updatePhotos = useCallback((patch: Partial<NonNullable<PlanSections['photos']>>) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const current = prev.photos || { coverPhotoId: null, items: [] };
      return { ...prev, photos: { ...current, ...patch } };
    });
    scheduleSave();
  }, [scheduleSave]);

  const updateDetails = useCallback((patch: Partial<PlanDetails>) => {
    setPlan((prev) => {
      if (!prev) return prev;
      const current = prev.details || { serviceLine: '', venue: '', celebrant: '' };
      return { ...prev, details: { ...current, ...patch } };
    });
    scheduleSave();
  }, [scheduleSave]);

  // PDF downloads (booklet, print-shop booklet, running sheet) — same
  // blob-download pattern as the editor exports.
  const [exporting, setExporting] = useState<string | null>(null);
  const downloadPdf = useCallback(async (kind: 'booklet' | 'booklet-bleed' | 'runsheet') => {
    if (!licenseId) return;
    setExporting(kind);
    try {
      const token = await getToken();
      const endpoint = kind === 'runsheet' ? '/api/service-plan/export-runsheet' : '/api/service-plan/export-booklet';
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId, ...(kind === 'booklet-bleed' ? { bleed: true } : {}) }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Download failed');
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = (personName || 'Service').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = kind === 'runsheet' ? `${base}_Running_Sheet.pdf` : `${base}_Order_of_Service${kind === 'booklet-bleed' ? '_PrintShop' : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErrMsg(e.message || 'Download failed — please try again.');
      setTimeout(() => setErrMsg(''), 5000);
    } finally {
      setExporting(null);
    }
  }, [licenseId, getToken, personName]);

  const startPurchase = useCallback(async () => {
    if (!licenseId) return;
    setBuying(true);
    setErrMsg('');
    try {
      const token = await getToken();
      const resp = await fetch('/api/stripe/create-funeral-guide-checkout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseId }),
      });
      const data = await resp.json();
      if (data.success && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      setErrMsg(data.message || data.error || 'We couldn’t start the purchase. Please try again.');
      setBuying(false);
    } catch {
      setErrMsg('We couldn’t reach the payment service. Please try again.');
      setBuying(false);
    }
  }, [licenseId, getToken]);

  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 14, padding: '1.75rem 1.5rem',
    boxShadow: '0 2px 14px rgba(25,40,92,0.07)', marginBottom: '1.5rem',
  };

  return (
    <div style={{ minHeight: '100vh', background: WARM, display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main style={{ flex: 1, width: '100%', maxWidth: 860, margin: '0 auto', padding: 'clamp(1rem, 3vw, 2rem)', boxSizing: 'border-box' }}>

        {phase === 'loading' && (
          <div style={{ textAlign: 'center', padding: '5rem 1rem', fontFamily: sans, color: GREY }}>
            Loading…
          </div>
        )}

        {phase === 'error' && (
          <div style={{ ...card, textAlign: 'center' }}>
            <p style={{ fontFamily: sans, color: BODY, marginBottom: '1rem' }}>{errMsg}</p>
            <Link to="/dashboard" style={{ fontFamily: sans, color: NAVY, fontWeight: 600 }}>Back to My Eulogies &amp; Tributes</Link>
          </div>
        )}

        {/* ---- License picker (teaser CTA lands here without an id) ---- */}
        {phase === 'pick' && (
          <>
            <h1 style={{ fontFamily: serif, color: NAVY, fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', marginBottom: '.5rem' }}>The Cyrano Funeral Guide</h1>
            <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.6, marginBottom: '1.5rem' }}>
              The guide attaches to a piece you’re writing, so everything — the service plan, the music, the printed booklet — is built around the person you’re honouring. Choose whose service you’re planning:
            </p>
            {licenses.length === 0 && (
              <div style={{ ...card, textAlign: 'center' }}>
                <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.6, marginBottom: '.75rem' }}>
                  <strong style={{ color: NAVY }}>The eulogy comes first — here’s why.</strong>
                </p>
                <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.65, maxWidth: 520, margin: '0 auto 1.25rem' }}>
                  The eulogy is the heart of any service, and everything in the Funeral Guide is built around it: the service plan weaves it in automatically, the printed booklet carries it, and the running order is timed to it. So the first step is to begin your eulogy or tribute — the guide will then be waiting for you, one button away on your dashboard.
                </p>
                <Link to="/new-project" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 1.5rem', background: NAVY, color: '#fff', borderRadius: 50, fontFamily: sans, fontWeight: 600, textDecoration: 'none' }}>
                  Begin a Eulogy or Tribute
                </Link>
              </div>
            )}
            {licenses.map((l) => (
              <Link key={l.id} to={`/funeral-guide/${l.id}`} style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', textDecoration: 'none', minHeight: 44 }}>
                <span style={{ fontFamily: serif, color: NAVY, fontSize: '1.15rem' }}>
                  {l.deceased_person_name || 'Unnamed piece'}
                </span>
                <span style={{ fontFamily: sans, fontSize: '.85rem', color: l.funeral_guide_purchased_at ? '#2a7d4f' : GREY }}>
                  {l.funeral_guide_purchased_at ? 'Guide unlocked ✓' : 'View the guide →'}
                </span>
              </Link>
            ))}
          </>
        )}

        {/* ---- Purchase card (402) ---- */}
        {phase === 'locked' && (
          <div style={{ ...card, textAlign: 'center', padding: '2.5rem 1.75rem' }}>
            <p style={{ fontFamily: sans, fontSize: '.8rem', letterSpacing: 3, textTransform: 'uppercase', color: GREY, marginBottom: '.75rem' }}>Every life, a story worth telling</p>
            <h1 style={{ fontFamily: serif, color: NAVY, fontSize: 'clamp(1.5rem, 4vw, 2rem)', marginBottom: '1rem' }}>
              Plan {personName ? `${personName}’s` : 'the'} service, step by step
            </h1>
            <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.65, maxWidth: 560, margin: '0 auto 1.5rem' }}>
              The complete guide from an experienced Australian funeral celebrant: how a service flows, a full running order with the words to say at each moment, a curated library of over 130 pieces of funeral music, and — coming with it — a service builder that produces a beautiful printed booklet for mourners and a running sheet for whoever leads the day.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', alignItems: 'center', marginBottom: '1.5rem' }}>
              {['The complete Funeral Guide — every section, from first phone call to final farewell', 'The full service template with the celebrant’s words for every step', 'The funeral music library — 130+ songs in five styles', 'The service builder, plus the printed booklet and running sheet'].map((f) => (
                <div key={f} style={{ fontFamily: sans, fontSize: '.95rem', color: BODY }}>
                  <span style={{ color: PINK, marginRight: 8 }}>✦</span>{f}
                </div>
              ))}
            </div>
            {content?.isOwner === false ? (
              <p style={{ fontFamily: sans, fontSize: '.9rem', color: GREY }}>
                Only the owner of this piece can unlock the guide — let them know you’d find it helpful.
              </p>
            ) : (
              <>
                <button onClick={startPurchase} disabled={buying}
                  style={{ minHeight: 48, padding: '0 2rem', background: NAVY, color: '#fff', border: 'none', borderRadius: 50, fontFamily: sans, fontWeight: 600, fontSize: '1rem', cursor: buying ? 'wait' : 'pointer' }}>
                  {buying ? 'Taking you to secure payment…' : 'Unlock the Funeral Guide — $99.95'}
                </button>
                <p style={{ fontFamily: sans, fontSize: '.8rem', color: GREY, marginTop: '.75rem' }}>
                  One-time payment · covers everything for {personName ? `${personName}’s` : 'this'} service · no subscription
                </p>
              </>
            )}
            {errMsg && <p style={{ fontFamily: sans, color: '#a33', marginTop: '1rem' }}>{errMsg}</p>}
          </div>
        )}

        {/* ---- The guide itself ---- */}
        {phase === 'unlocked' && content?.guide && content?.music && (
          <>
            <p style={{ fontFamily: sans, fontSize: '.8rem', letterSpacing: 3, textTransform: 'uppercase', color: GREY, marginBottom: '.4rem' }}>The Cyrano Funeral Guide</p>
            <h1 style={{ fontFamily: serif, color: NAVY, fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', marginBottom: '1.25rem' }}>
              {personName ? `A service for ${personName}` : 'Planning the service'}
            </h1>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              {([['guide', 'The Guide', 'book'], ['template', 'Service Template', 'list'], ['music', 'Music Library', 'music'], ['build', 'Build the Service', 'write']] as const).map(([key, label, icon]) => (
                <button key={key} onClick={() => setTab(key)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 1.1rem',
                    borderRadius: 50, border: `1px solid ${tab === key ? NAVY : 'rgba(25,40,92,0.25)'}`,
                    background: tab === key ? NAVY : '#fff', color: tab === key ? '#fff' : NAVY,
                    fontFamily: sans, fontWeight: 600, fontSize: '.92rem', cursor: 'pointer',
                  }}>
                  <img src={ICON(icon)} alt="" style={{ width: 20, height: 20, filter: tab === key ? 'brightness(0) invert(1)' : NAVY_FILTER }} />
                  {label}
                </button>
              ))}
            </div>

            {tab === 'guide' && content.guide.sections.map((s) => (
              <section key={s.id} style={card}>
                <p style={{ fontFamily: sans, fontSize: '.78rem', letterSpacing: 2, color: PINK, fontWeight: 700, marginBottom: '.3rem' }}>{s.number}</p>
                <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.35rem', marginBottom: '.75rem' }}>{s.title}</h2>
                {s.paragraphs.map((p, i) => (
                  <p key={i} style={{ fontFamily: sans, color: BODY, lineHeight: 1.7, marginBottom: '.85rem' }}>{p}</p>
                ))}
                {s.callout && (
                  <div style={{ background: 'rgba(212,173,187,0.14)', border: '1px solid rgba(212,173,187,0.45)', borderRadius: 10, padding: '.9rem 1.1rem', marginTop: '.5rem' }}>
                    <strong style={{ fontFamily: sans, color: NAVY, display: 'block', marginBottom: '.25rem' }}>{s.callout.title}</strong>
                    <span style={{ fontFamily: sans, color: BODY, lineHeight: 1.6 }}>{s.callout.text}</span>
                  </div>
                )}
                {s.tips?.map((t) => (
                  <div key={t.title} style={{ borderLeft: `3px solid ${PINK}`, paddingLeft: '1rem', marginTop: '.85rem' }}>
                    <strong style={{ fontFamily: sans, color: NAVY, display: 'block', marginBottom: '.2rem' }}>{t.title}</strong>
                    <span style={{ fontFamily: sans, color: BODY, lineHeight: 1.6, fontSize: '.95rem' }}>{t.text}</span>
                  </div>
                ))}
              </section>
            ))}

            {tab === 'template' && (
              <>
                <div style={{ ...card, background: 'rgba(212,173,187,0.12)', border: '1px solid rgba(212,173,187,0.4)', boxShadow: 'none' }}>
                  <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.65, margin: 0 }}>
                    This is how a typical humanist funeral flows — a guide, not a rulebook. There is no wrong or right way, and your funeral arranger can help with every aspect. The interactive builder (coming to this page next) will let you fill each step in for {personName || 'your loved one'} and print the finished booklet.
                  </p>
                </div>
                {content.guide.template.map((el, i) => (
                  <section key={el.id} style={card}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: '.5rem' }}>
                      <span style={{ fontFamily: serif, color: PINK, fontSize: '1.2rem', fontWeight: 700 }}>{i + 1}</span>
                      <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.2rem', margin: 0 }}>{el.title}</h2>
                      {el.optional && <span style={{ fontFamily: sans, fontSize: '.72rem', letterSpacing: 1, color: GREY, textTransform: 'uppercase' }}>Optional</span>}
                    </div>
                    <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.65, marginBottom: el.script || el.musicCue ? '.75rem' : 0 }}>{el.guidance}</p>
                    {el.script && (
                      <div style={{ borderLeft: `3px solid ${GREY}`, paddingLeft: '1rem', marginBottom: el.musicCue ? '.75rem' : 0 }}>
                        <p style={{ fontFamily: sans, fontSize: '.78rem', letterSpacing: 2, color: GREY, textTransform: 'uppercase', marginBottom: '.35rem' }}>Example words</p>
                        {el.script.split('\n\n').map((line, li) => (
                          <p key={li} style={{ fontFamily: serif, fontStyle: 'italic', color: NAVY, lineHeight: 1.65, marginBottom: '.6rem', fontSize: '.98rem' }}>{line}</p>
                        ))}
                      </div>
                    )}
                    {el.musicCue && (
                      <p style={{ fontFamily: sans, fontSize: '.9rem', color: GREY, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                        <img src={ICON('music')} alt="" style={{ width: 18, height: 18, filter: NAVY_FILTER, opacity: .7 }} />
                        {el.musicCue}
                      </p>
                    )}
                  </section>
                ))}
              </>
            )}

            {tab === 'music' && (
              <>
                <section style={card}>
                  {content.music.intro.map((p, i) => (
                    <p key={i} style={{ fontFamily: sans, color: BODY, lineHeight: 1.7, marginBottom: i === content.music!.intro.length - 1 ? 0 : '.85rem' }}>{p}</p>
                  ))}
                </section>
                <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <button onClick={() => setMusicFilter('all')}
                    style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: `1px solid ${musicFilter === 'all' ? NAVY : 'rgba(25,40,92,0.25)'}`, background: musicFilter === 'all' ? NAVY : '#fff', color: musicFilter === 'all' ? '#fff' : NAVY, fontFamily: sans, fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
                    All styles
                  </button>
                  {content.music.categories.map((c) => (
                    <button key={c.key} onClick={() => setMusicFilter(c.key)}
                      style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: `1px solid ${musicFilter === c.key ? NAVY : 'rgba(25,40,92,0.25)'}`, background: musicFilter === c.key ? NAVY : '#fff', color: musicFilter === c.key ? '#fff' : NAVY, fontFamily: sans, fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {content.music.categories.filter((c) => musicFilter === 'all' || c.key === musicFilter).map((c) => (
                  <section key={c.key} style={card}>
                    <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.25rem', marginBottom: '1rem' }}>{c.label}</h2>
                    {c.songs.map((s) => (
                      <div key={`${s.title}|${s.artist}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '.55rem 0', borderBottom: '1px solid rgba(25,40,92,0.08)', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontFamily: sans, color: BODY, fontWeight: 600 }}>{s.title}</span>
                          {s.artist && <span style={{ fontFamily: sans, color: GREY }}> — {s.artist}</span>}
                        </div>
                        <a href={s.amazonUrl} target="_blank" rel="noopener noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 .9rem', fontFamily: sans, fontSize: '.82rem', fontWeight: 600, color: NAVY, border: '1px solid rgba(25,40,92,0.3)', borderRadius: 50, textDecoration: 'none', whiteSpace: 'nowrap' }}>
                          Find on Amazon
                        </a>
                      </div>
                    ))}
                  </section>
                ))}
                <p style={{ fontFamily: sans, fontSize: '.78rem', color: GREY, lineHeight: 1.5, marginBottom: '2rem' }}>
                  Amazon links may earn Cyrano a small commission at no cost to you. Music plays from your own accounts and purchases — Cyrano never hosts audio.
                </p>
              </>
            )}

            {tab === 'build' && !plan && (
              <div style={{ ...card, textAlign: 'center', fontFamily: sans, color: GREY }}>
                Opening {personName ? `${personName}’s` : 'the'} service plan…
              </div>
            )}

            {tab === 'build' && plan && (
              <>
                <div style={{ ...card, background: 'rgba(212,173,187,0.12)', border: '1px solid rgba(212,173,187,0.4)', boxShadow: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <p style={{ fontFamily: sans, color: BODY, lineHeight: 1.6, margin: 0, flex: '1 1 260px' }}>
                    Work through each part of {personName ? `${personName}’s` : 'the'} service below — the words are started for you, and everything can be changed. Nothing is ever locked in: you can come back and change any of these details at any time, and your plan saves itself as you go{canWrite ? '' : ' (you have read-only access to this plan)'}.
                  </p>
                  <span style={{ fontFamily: sans, fontSize: '.82rem', color: saveState === 'error' ? '#a33' : GREY, whiteSpace: 'nowrap' }}>
                    {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'All changes saved ✓' : saveState === 'error' ? 'Couldn’t save — check your connection' : ''}
                  </span>
                </div>

                {/* Service details — feeds the booklet cover and the running sheet */}
                <section style={card}>
                  <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.2rem', marginBottom: '.5rem' }}>The service details</h2>
                  <p style={{ fontFamily: sans, color: GREY, fontSize: '.92rem', lineHeight: 1.6, marginBottom: '.85rem' }}>
                    These appear on the booklet cover and the running sheet. Fill in what you know — everything can wait until it’s confirmed.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {([
                      ['serviceLine', 'Date and time', 'e.g. Saturday, 21 June 2026 at 11.00am'],
                      ['venue', 'Venue', 'e.g. St Andrew’s Chapel, Bathurst'],
                      ['celebrant', 'Who is leading the service', 'e.g. Anne Walker, celebrant — or a family member'],
                    ] as const).map(([key, label, ph]) => (
                      <div key={key}>
                        <label style={{ fontFamily: sans, fontSize: '.85rem', fontWeight: 600, color: NAVY, display: 'block', marginBottom: 6 }}>{label}</label>
                        <input value={plan.details?.[key] || ''} disabled={!canWrite} spellCheck placeholder={ph}
                          onChange={(e) => updateDetails({ [key]: e.target.value } as any)}
                          style={{ width: '100%', boxSizing: 'border-box', padding: '12px', border: '1px solid rgba(25,40,92,0.25)', borderRadius: 8, fontFamily: sans, fontSize: 16, color: BODY, background: canWrite ? '#fff' : WARM }} />
                      </div>
                    ))}
                  </div>
                </section>

                {content.guide.template.map((el, i) => {
                  const st = plan.elements[el.id] || { included: true };
                  const inputStyle: React.CSSProperties = {
                    width: '100%', boxSizing: 'border-box', padding: '12px',
                    border: '1px solid rgba(25,40,92,0.25)', borderRadius: 8,
                    fontFamily: sans, fontSize: 16, color: BODY, background: canWrite ? '#fff' : WARM,
                  };
                  return (
                    <section key={el.id} style={{ ...card, opacity: st.included ? 1 : 0.55 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: serif, color: PINK, fontSize: '1.2rem', fontWeight: 700 }}>{i + 1}</span>
                        <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.2rem', margin: 0, flex: 1 }}>{el.title}</h2>
                        {el.optional && canWrite && (
                          <button onClick={() => updateElement(el.id, { included: !st.included })}
                            style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: `1px solid ${st.included ? NAVY : 'rgba(25,40,92,0.3)'}`, background: st.included ? NAVY : '#fff', color: st.included ? '#fff' : GREY, fontFamily: sans, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer' }}>
                            {st.included ? 'Included ✓' : 'Skipped — tap to include'}
                          </button>
                        )}
                      </div>
                      <p style={{ fontFamily: sans, color: GREY, fontSize: '.92rem', lineHeight: 1.6, marginBottom: st.included ? '.85rem' : 0 }}>{el.guidance}</p>

                      {st.included && (
                        <>
                          {el.id === 'eulogy' && (
                            <>
                              <div style={{ background: 'rgba(140,131,180,0.1)', borderRadius: 8, padding: '.75rem 1rem', marginBottom: '.75rem', fontFamily: sans, fontSize: '.92rem', color: BODY }}>
                                {personName ? `${personName}’s` : 'The'} eulogy comes straight from your Cyrano piece — nothing to re-enter. The Speaker’s Copy export gives the reader large print for the lectern.
                              </div>
                              <label style={{ fontFamily: sans, fontSize: '.85rem', fontWeight: 600, color: NAVY, display: 'block', marginBottom: 6 }}>Who will read the eulogy?</label>
                              <input value={st.reader || ''} disabled={!canWrite} spellCheck
                                onChange={(e) => updateElement(el.id, { reader: e.target.value })}
                                placeholder="e.g. Sarah (daughter), or the celebrant" style={inputStyle} />
                              <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: sans, fontSize: '.92rem', color: BODY, marginTop: '.75rem', minHeight: 44, cursor: canWrite ? 'pointer' : 'default' }}>
                                <input type="checkbox" checked={st.printInBooklet !== false} disabled={!canWrite}
                                  onChange={(e) => updateElement(el.id, { printInBooklet: e.target.checked })}
                                  style={{ width: 20, height: 20 }} />
                                Print the full eulogy in the booklet (untick to list it in the order of service only)
                              </label>
                            </>
                          )}

                          {el.id === 'tributes' && (
                            <>
                              {(st.speakers || []).map((sp, si) => (
                                <div key={si} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                  <input value={sp.name} disabled={!canWrite} placeholder="Name"
                                    onChange={(e) => {
                                      const speakers = [...(st.speakers || [])];
                                      speakers[si] = { ...speakers[si], name: e.target.value };
                                      updateElement(el.id, { speakers });
                                    }}
                                    style={{ ...inputStyle, flex: '2 1 160px', width: 'auto' }} />
                                  <input value={sp.relationship} disabled={!canWrite} placeholder="Relationship (e.g. grandson)"
                                    onChange={(e) => {
                                      const speakers = [...(st.speakers || [])];
                                      speakers[si] = { ...speakers[si], relationship: e.target.value };
                                      updateElement(el.id, { speakers });
                                    }}
                                    style={{ ...inputStyle, flex: '2 1 160px', width: 'auto' }} />
                                  {canWrite && (
                                    <button onClick={() => updateElement(el.id, { speakers: (st.speakers || []).filter((_, x) => x !== si) })}
                                      aria-label="Remove speaker"
                                      style={{ minHeight: 44, minWidth: 44, border: '1px solid rgba(25,40,92,0.25)', borderRadius: 8, background: '#fff', color: GREY, cursor: 'pointer', fontSize: 18 }}>×</button>
                                  )}
                                </div>
                              ))}
                              {canWrite && (
                                <button onClick={() => updateElement(el.id, { speakers: [...(st.speakers || []), { name: '', relationship: '' }] })}
                                  style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: '1px solid rgba(25,40,92,0.3)', background: '#fff', color: NAVY, fontFamily: sans, fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
                                  + Add a speaker (~3 minutes each)
                                </button>
                              )}
                            </>
                          )}

                          {el.id === 'readings' && (
                            <>
                              {(st.readings || []).map((rd, ri) => (
                                <div key={ri} style={{ border: '1px solid rgba(25,40,92,0.15)', borderRadius: 10, padding: '.85rem', marginBottom: 10 }}>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                    <input value={rd.title} disabled={!canWrite} placeholder="Title (e.g. Death Is Nothing at All)"
                                      onChange={(e) => { const readings = [...(st.readings || [])]; readings[ri] = { ...readings[ri], title: e.target.value }; updateElement(el.id, { readings }); }}
                                      style={{ ...inputStyle, flex: '2 1 200px', width: 'auto' }} />
                                    <input value={rd.author} disabled={!canWrite} placeholder="Author"
                                      onChange={(e) => { const readings = [...(st.readings || [])]; readings[ri] = { ...readings[ri], author: e.target.value }; updateElement(el.id, { readings }); }}
                                      style={{ ...inputStyle, flex: '1 1 140px', width: 'auto' }} />
                                    <input value={rd.readBy} disabled={!canWrite} placeholder="Read by"
                                      onChange={(e) => { const readings = [...(st.readings || [])]; readings[ri] = { ...readings[ri], readBy: e.target.value }; updateElement(el.id, { readings }); }}
                                      style={{ ...inputStyle, flex: '1 1 140px', width: 'auto' }} />
                                    {canWrite && (
                                      <button onClick={() => updateElement(el.id, { readings: (st.readings || []).filter((_, x) => x !== ri) })}
                                        aria-label="Remove reading"
                                        style={{ minHeight: 44, minWidth: 44, border: '1px solid rgba(25,40,92,0.25)', borderRadius: 8, background: '#fff', color: GREY, cursor: 'pointer', fontSize: 18 }}>×</button>
                                    )}
                                  </div>
                                  <textarea value={rd.text} disabled={!canWrite} spellCheck rows={3}
                                    onChange={(e) => { const readings = [...(st.readings || [])]; readings[ri] = { ...readings[ri], text: e.target.value }; updateElement(el.id, { readings }); }}
                                    placeholder="The full text — only if you'd like it printed in the booklet. For a poem still in copyright, leave this empty and the booklet lists the title and author."
                                    style={{ ...inputStyle, fontFamily: 'Georgia, serif', resize: 'vertical', lineHeight: 1.6 }} />
                                </div>
                              ))}
                              {canWrite && (
                                <button onClick={() => updateElement(el.id, { readings: [...(st.readings || []), { title: '', author: '', readBy: '', text: '' }] })}
                                  style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: '1px solid rgba(25,40,92,0.3)', background: '#fff', color: NAVY, fontFamily: sans, fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
                                  + Add a reading or poem
                                </button>
                              )}
                            </>
                          )}

                          {el.id === 'custom-moments' && (
                            <>
                              {(st.moments || []).map((mo, mi) => (
                                <div key={mi} style={{ border: '1px solid rgba(25,40,92,0.15)', borderRadius: 10, padding: '.85rem', marginBottom: 10 }}>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                                    <input value={mo.title} disabled={!canWrite} placeholder="What happens (e.g. Bagpipes: Amazing Grace)"
                                      onChange={(e) => { const moments = [...(st.moments || [])]; moments[mi] = { ...moments[mi], title: e.target.value }; updateElement(el.id, { moments }); }}
                                      style={{ ...inputStyle, flex: '2 1 220px', width: 'auto' }} />
                                    {canWrite && (
                                      <button onClick={() => updateElement(el.id, { moments: (st.moments || []).filter((_, x) => x !== mi) })}
                                        aria-label="Remove moment"
                                        style={{ minHeight: 44, minWidth: 44, border: '1px solid rgba(25,40,92,0.25)', borderRadius: 8, background: '#fff', color: GREY, cursor: 'pointer', fontSize: 18 }}>×</button>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                    <input value={mo.detail} disabled={!canWrite} placeholder="Any detail for the booklet (who, what to expect)"
                                      onChange={(e) => { const moments = [...(st.moments || [])]; moments[mi] = { ...moments[mi], detail: e.target.value }; updateElement(el.id, { moments }); }}
                                      style={{ ...inputStyle, flex: '2 1 220px', width: 'auto' }} />
                                    <input value={mo.music} disabled={!canWrite} placeholder="Music, if any"
                                      onChange={(e) => { const moments = [...(st.moments || [])]; moments[mi] = { ...moments[mi], music: e.target.value }; updateElement(el.id, { moments }); }}
                                      style={{ ...inputStyle, flex: '1 1 160px', width: 'auto' }} />
                                  </div>
                                </div>
                              ))}
                              {canWrite && (
                                <button onClick={() => updateElement(el.id, { moments: [...(st.moments || []), { title: '', detail: '', music: '' }] })}
                                  style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: '1px solid rgba(25,40,92,0.3)', background: '#fff', color: NAVY, fontFamily: sans, fontSize: '.88rem', fontWeight: 600, cursor: 'pointer' }}>
                                  + Add a moment of your own
                                </button>
                              )}
                            </>
                          )}

                          {el.id === 'housekeeping' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: sans, fontSize: '.92rem', color: BODY, marginBottom: '.75rem', minHeight: 44, cursor: canWrite ? 'pointer' : 'default' }}>
                              <input type="checkbox" checked={!!st.livestream} disabled={!canWrite}
                                onChange={(e) => updateElement(el.id, { livestream: e.target.checked })}
                                style={{ width: 20, height: 20 }} />
                              Some family or friends will be joining by live stream
                            </label>
                          )}

                          {(el.id === 'welcome' || el.id === 'farewell') && (
                            <>
                              <label style={{ fontFamily: sans, fontSize: '.85rem', fontWeight: 600, color: NAVY, display: 'block', marginBottom: 6 }}>
                                {el.id === 'welcome' ? 'Notes for the welcome (family names, who is speaking, anything the celebrant should mention)' : 'Wake or gathering details, thank-yous, and any closing reading'}
                              </label>
                              <textarea value={st.notes || ''} disabled={!canWrite} spellCheck rows={3}
                                onChange={(e) => updateElement(el.id, { notes: e.target.value })}
                                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} />
                            </>
                          )}

                          {el.script !== undefined && plan.elements[el.id]?.script !== undefined && (
                            <>
                              <label style={{ fontFamily: sans, fontSize: '.85rem', fontWeight: 600, color: NAVY, display: 'block', margin: '.75rem 0 6px' }}>
                                The words — started for you, change anything you like
                              </label>
                              <textarea value={st.script || ''} disabled={!canWrite} spellCheck rows={Math.min(10, Math.max(4, (st.script || '').split('\n').length + 1))}
                                onChange={(e) => updateElement(el.id, { script: e.target.value })}
                                style={{ ...inputStyle, fontFamily: 'Georgia, serif', resize: 'vertical', lineHeight: 1.65 }} />
                            </>
                          )}

                          {el.musicCue && (
                            <>
                              <label style={{ fontFamily: sans, fontSize: '.85rem', fontWeight: 600, color: NAVY, display: 'block', margin: '.75rem 0 6px' }}>
                                Music — {el.musicCue.charAt(0).toLowerCase()}{el.musicCue.slice(1)}
                              </label>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <input value={st.music || ''} disabled={!canWrite} spellCheck
                                  onChange={(e) => updateElement(el.id, { music: e.target.value })}
                                  placeholder="Song title — artist" style={{ ...inputStyle, flex: '1 1 220px', width: 'auto' }} />
                                {canWrite && (
                                  <button onClick={() => { setPickerFor(pickerFor === el.id ? null : el.id); setPickerFilter('all'); }}
                                    style={{ minHeight: 44, padding: '0 1rem', borderRadius: 50, border: `1px solid ${pickerFor === el.id ? NAVY : 'rgba(25,40,92,0.3)'}`, background: pickerFor === el.id ? NAVY : '#fff', color: pickerFor === el.id ? '#fff' : NAVY, fontFamily: sans, fontSize: '.85rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    {pickerFor === el.id ? 'Close the library' : 'Choose from the library'}
                                  </button>
                                )}
                              </div>
                              {pickerFor === el.id && content.music && (
                                <div style={{ border: '1px solid rgba(25,40,92,0.15)', borderRadius: 10, marginTop: 10, padding: '.85rem', background: '#fdfcfb' }}>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '.6rem' }}>
                                    {[{ key: 'all', label: 'All' }, ...content.music.categories].map((c: any) => (
                                      <button key={c.key} onClick={() => setPickerFilter(c.key)}
                                        style={{ minHeight: 36, padding: '0 .8rem', borderRadius: 50, border: `1px solid ${pickerFilter === c.key ? NAVY : 'rgba(25,40,92,0.2)'}`, background: pickerFilter === c.key ? NAVY : '#fff', color: pickerFilter === c.key ? '#fff' : NAVY, fontFamily: sans, fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}>
                                        {c.label}
                                      </button>
                                    ))}
                                  </div>
                                  <div style={{ maxHeight: 230, overflowY: 'auto' }}>
                                    {content.music.categories.filter((c) => pickerFilter === 'all' || c.key === pickerFilter).flatMap((c) => c.songs).map((s) => (
                                      <button key={`${s.title}|${s.artist}`}
                                        onClick={() => { updateElement(el.id, { music: s.artist ? `${s.title} — ${s.artist}` : s.title }); setPickerFor(null); }}
                                        style={{ display: 'block', width: '100%', textAlign: 'left', minHeight: 44, padding: '.5rem .6rem', border: 'none', borderBottom: '1px solid rgba(25,40,92,0.06)', background: 'transparent', cursor: 'pointer', fontFamily: sans, fontSize: '.9rem', color: BODY }}>
                                        <strong>{s.title}</strong>{s.artist ? <span style={{ color: GREY }}> — {s.artist}</span> : null}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </section>
                  );
                })}

                {/* Photos for the booklet — fully adaptive: none chosen means
                    the booklet simply has no photo pages; many chosen flow
                    across as many pages as needed. */}
                <section style={card}>
                  <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.2rem', marginBottom: '.5rem' }}>Photos for the booklet</h2>
                  <p style={{ fontFamily: sans, color: GREY, fontSize: '.92rem', lineHeight: 1.6, marginBottom: '.85rem' }}>
                    Choose as many or as few as you like — the booklet shapes itself around them. Tap a photo to include it, add a caption, and choose one for the cover. No photos chosen is fine too.
                  </p>
                  {libraryPhotos.length === 0 ? (
                    <p style={{ fontFamily: sans, fontSize: '.92rem', color: BODY, lineHeight: 1.6, margin: 0 }}>
                      No photos uploaded yet — add them from your piece in CyranoEditor (Photos tab), or from the family memories page, and they’ll appear here.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                      {libraryPhotos.map((ph) => {
                        const sel = (plan.photos?.items || []).find((x) => x.id === ph.id);
                        const isCover = plan.photos?.coverPhotoId === ph.id;
                        const src = ph.thumbnail_url || ph.restored_url || ph.public_url;
                        return (
                          <div key={ph.id} style={{ width: 148 }}>
                            <button disabled={!canWrite}
                              onClick={() => {
                                const items = [...(plan.photos?.items || [])];
                                const idx = items.findIndex((x) => x.id === ph.id);
                                if (idx > -1) {
                                  items.splice(idx, 1);
                                  updatePhotos({ items, ...(isCover ? { coverPhotoId: null } : {}) });
                                } else {
                                  items.push({ id: ph.id, caption: ph.caption || '' });
                                  updatePhotos({ items });
                                }
                              }}
                              style={{ display: 'block', width: '100%', padding: 0, border: sel ? `3px solid ${NAVY}` : '3px solid transparent', borderRadius: 10, overflow: 'hidden', cursor: canWrite ? 'pointer' : 'default', background: 'none', position: 'relative' }}>
                              <img src={src} alt="" style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block', opacity: sel ? 1 : 0.75 }} />
                              {sel && <span style={{ position: 'absolute', top: 6, right: 6, background: NAVY, color: '#fff', borderRadius: 12, fontSize: 11, fontFamily: sans, padding: '2px 8px' }}>✓</span>}
                            </button>
                            {sel && (
                              <>
                                <input value={sel.caption} disabled={!canWrite} placeholder="Caption (optional)" spellCheck
                                  onChange={(e) => {
                                    const items = (plan.photos?.items || []).map((x) => x.id === ph.id ? { ...x, caption: e.target.value } : x);
                                    updatePhotos({ items });
                                  }}
                                  style={{ width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '8px', border: '1px solid rgba(25,40,92,0.2)', borderRadius: 6, fontFamily: sans, fontSize: 16 }} />
                                <button disabled={!canWrite}
                                  onClick={() => updatePhotos({ coverPhotoId: isCover ? null : ph.id })}
                                  style={{ marginTop: 6, width: '100%', minHeight: 36, borderRadius: 50, border: `1px solid ${isCover ? NAVY : 'rgba(25,40,92,0.25)'}`, background: isCover ? NAVY : '#fff', color: isCover ? '#fff' : NAVY, fontFamily: sans, fontSize: '.78rem', fontWeight: 600, cursor: canWrite ? 'pointer' : 'default' }}>
                                  {isCover ? 'On the cover ✓' : 'Use on the cover'}
                                </button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {/* The printed pieces — created from this plan */}
                <section style={card}>
                  <h2 style={{ fontFamily: serif, color: NAVY, fontSize: '1.2rem', marginBottom: '.5rem' }}>Your printed pieces</h2>
                  <p style={{ fontFamily: sans, color: GREY, fontSize: '.92rem', lineHeight: 1.6, marginBottom: '1rem' }}>
                    Created fresh from the plan above each time you download — change anything and simply download again.
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {([
                      ['booklet', 'Order of Service booklet (A5 PDF)', 'For mourners — cover, order of service, eulogy, photos, farewell words'],
                      ['booklet-bleed', 'Print-shop version (3mm bleed + trim marks)', 'If a professional printer asks for a “bleed” version, this is the one'],
                      ['runsheet', 'Celebrant’s running sheet (A4 PDF)', 'Every script, cue and speaker for whoever leads the day'],
                    ] as const).map(([kind, label, desc]) => (
                      <button key={kind} onClick={() => downloadPdf(kind)} disabled={!!exporting}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minHeight: 56, padding: '.75rem 1.1rem', borderRadius: 10, border: `1px solid ${exporting === kind ? NAVY : 'rgba(25,40,92,0.3)'}`, background: '#fff', cursor: exporting ? 'wait' : 'pointer', textAlign: 'left', opacity: exporting && exporting !== kind ? 0.5 : 1 }}>
                        <span style={{ fontFamily: sans, fontWeight: 600, fontSize: '.95rem', color: NAVY }}>
                          {exporting === kind ? 'Preparing your PDF…' : label}
                        </span>
                        <span style={{ fontFamily: sans, fontSize: '.82rem', color: GREY }}>{desc}</span>
                      </button>
                    ))}
                  </div>
                  {errMsg && <p style={{ fontFamily: sans, color: '#a33', fontSize: '.9rem', marginTop: '.75rem' }}>{errMsg}</p>}
                </section>

                {/* Printing guidance — funeral timelines make LOCAL printing the
                    reliable path (post-funeral keepsakes are a different story). */}
                <div style={{ ...card, background: 'rgba(140,131,180,0.08)', boxShadow: 'none' }}>
                  <h3 style={{ fontFamily: serif, color: NAVY, fontSize: '1.05rem', marginBottom: '.5rem' }}>Printing your booklet</h3>
                  <p style={{ fontFamily: sans, color: BODY, fontSize: '.92rem', lineHeight: 1.65, margin: 0 }}>
                    <strong>Your funeral home</strong> — many arrange printing as part of their service; simply email them the PDF.&nbsp;
                    <strong>Same-day printing</strong> — Officeworks and similar print centres take the standard PDF online and have folded booklets ready the same day.&nbsp;
                    <strong>A local printer</strong> — send the print-shop version if they ask for bleed. A5 folded, saddle-stitched, on a nice matte stock is the classic choice.
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
